import { Download, Loader2 } from "lucide-react";
import { Button } from "./ui/button";

/**
 * Shared navy "Download PDF" button treatment. Extracted from
 * `SupportTools.jsx` (Jul 13 2026) so every download affordance on
 * the marketing surface reads as one consistent utility — mint
 * remains the primary-action colour ("Try for free", "Start
 * Building"), navy is the utility/download colour.
 *
 * Usage:
 *   <DownloadPdfButton
 *     href="/api/strategies/critical-thinking/pdf"
 *     testId="strategy-download-pdf-critical-thinking"
 *     label="Download this playbook (PDF)"
 *   />
 *
 *   // Or for onClick-driven downloads with a loading state (used
 *   // on /support-tools where the backend generates a fresh PDF
 *   // and returns it as a blob):
 *   <DownloadPdfButton
 *     onClick={handleDownload}
 *     loading={downloading}
 *     testId="support-tool-download-pack"
 *     label="Download the full pack — PDF"
 *     size="lg"
 *   />
 *
 * Sizes: "md" (default, h-10) matches the per-card treatment.
 *        "lg" (h-12) matches the hero-band pack download.
 */
export default function DownloadPdfButton({
  href,
  onClick,
  loading = false,
  disabled = false,
  testId,
  label,
  loadingLabel = "Preparing…",
  icon: Icon = Download,
  size = "md",
  className = "",
}) {
  const sizeClasses = size === "lg" ? "h-12 px-6 text-base" : "h-10 px-4 text-sm";
  const iconSize = size === "lg" ? "h-5 w-5" : "h-4 w-4";
  const shared =
    `${sizeClasses} rounded-md font-extrabold inline-flex items-center ` +
    "justify-center gap-2 bg-[#1c2d3d] text-white hover:bg-[#0f1e2c] " +
    "disabled:opacity-60 transition-colors";

  const content = loading ? (
    <>
      <Loader2 className={`${iconSize} animate-spin`} />
      {loadingLabel}
    </>
  ) : (
    <>
      <Icon className={iconSize} />
      {label}
    </>
  );

  if (href) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={testId}
        className={`${shared} ${className}`}
      >
        {content}
      </a>
    );
  }

  return (
    <Button
      type="button"
      onClick={onClick}
      disabled={disabled || loading}
      data-testid={testId}
      className={`${shared} ${className}`}
    >
      {content}
    </Button>
  );
}
